import { createContext } from 'react';
import type { FormField } from '@features/ksp-actions/ActionForm';
import type { KspWorkspace } from '@entities/ksp/types';

/** Вкладки (значение `?tab=`). Адреса прежние — на них ведут ссылки из других модулей. */
export const TABS = [
  ['overview', 'Сводка'],
  ['sources', 'Исходные данные'],
  ['schedule', 'График'],
  ['rd', 'Даты РД'],
  ['periods', 'Месячные задания'],
  ['actual', 'Выполнение'],
  ['planfact', 'План-факт и кривые'],
  ['milestones', 'Вехи'],
  ['recovery', 'Ликвидация отставания'],
  ['documents', 'Документы'],
  ['history', 'История'],
] as const;

export type KspTab = (typeof TABS)[number][0];

/**
 * Разделы по ходу работы инженера: 11 вкладок в 5 разделах и отдельные «Данные
 * проекта» (загрузка, настройки — нужны редко). Первая вкладка раздела открывается
 * по щелчку на раздел.
 */
export const SECTIONS: { id: string; title: string; tabs: KspTab[]; aside?: boolean }[] = [
  { id: 'summary', title: 'Сводка', tabs: ['overview'] },
  { id: 'schedule', title: 'График', tabs: ['schedule', 'rd'] },
  { id: 'execution', title: 'Исполнение', tabs: ['periods', 'actual', 'planfact'] },
  { id: 'milestones', title: 'Вехи', tabs: ['milestones', 'recovery'] },
  { id: 'documents', title: 'Документы', tabs: ['documents', 'history'] },
  { id: 'data', title: 'Данные проекта', tabs: ['sources'], aside: true },
];

export const tabTitle = (tab: string) => TABS.find(([id]) => id === tab)?.[1] ?? tab;
/** Ссылка на вкладку с сохранением выбранной версии, снимка и периода. */
export const tabHref = (params: URLSearchParams, tab: string) =>
  `?${new URLSearchParams({ ...Object.fromEntries(params), tab })}`;
export const sectionOf = (tab: string) =>
  SECTIONS.find((section) => (section.tabs as string[]).includes(tab)) ?? SECTIONS[0];

export const textField = (key: string, title: string, required = true, value = ''): FormField => ({
  key,
  label: title,
  required,
  value,
});

// Документы, которые печатаются по конкретному объекту (заданию периода, акту, плану, письму).
export const OBJECT_EXPORTS = ['period_plan', 'deviation_act', 'recovery_plan', 'letter'];

export const choice = (
  key: string,
  title: string,
  options: { value: string; label: string }[],
  value = '',
): FormField => ({ key, label: title, type: 'select', options, required: true, value });

export const dateField = (key: string, title: string, value = ''): FormField => ({
  key,
  label: title,
  type: 'date',
  required: true,
  value,
});

export const optional = (key: string, value: string) => (value ? { [key]: value } : {});

export const rdCatalogue = (values: Record<string, string>) => ({
  expected_rd_codes: values.expected_rd_codes.trim()
    ? values.expected_rd_codes
        .split(/\r?\n/)
        .map((code) => code.trim())
        .filter(Boolean)
    : null,
  expected_rd_basis: values.expected_rd_basis.trim() || null,
});

export const sourceText = (source?: {
  kind: string;
  record_id: string;
  revision: string;
  locator?: string | null;
}) =>
  source
    ? `${source.kind} · ${source.record_id} · ${source.revision}${source.locator ? ` · ${source.locator}` : ''}`
    : 'Источник не указан';

export const CollectionContext = createContext<{
  workspace: KspWorkspace;
  loading: string | null;
  load: (collection: string) => void;
} | null>(null);

export const collectionNames: Record<string, string> = {
  activities: 'Работы',
  dependencies: 'Связи',
  calendars: 'Календари',
  resources: 'Ресурсы',
  rd_bindings: 'РД',
  supply_bindings: 'Поставки',
  ledger: 'Принятый факт',
  actual_submissions: 'Поданные факты',
  audit_events: 'История',
  calculations: 'Расчёты',
  imports: 'Импорты',
  period_plans: 'Месячные задания',
  versions: 'Версии',
  milestones: 'Вехи',
  alerts: 'Предупреждения',
  letters: 'Письма',
  evidence: 'Подтверждения отправки',
  recovery_plans: 'Планы восстановления',
  deviation_acts: 'Акты отклонений',
};
